import React, { useEffect, useRef, useState, useCallback } from "react";
import { Flex } from "@jsq/ds/core";
import { TreeWrapper, TreeContainer } from "./style_vanilla.css";
import { TreeEdge } from "./TreeEdge";
import { TreeNode } from "./TreeNode";
import { TreeConfig, EdgeStyle, Node } from "./types";
import { edgeConfig } from "./util";

export type TreeProps = {
  data: Node;
  treeConfig?: TreeConfig;
  edgeStyle?: EdgeStyle;
  rootEdgeStyle?: EdgeStyle;
};

export const TreeComponent = (props: TreeProps) => {
  const treeWrapper = useRef<HTMLDivElement>(null);
  const [containerBounds, setContainerBounds] = useState<DOMRect>();
  const [scrollOffset, setScrollOffset] = useState({ x: 0, y: 0 });

  const updateContainerBounds = useCallback(() => {
    if (treeWrapper.current) {
      const newBounds = treeWrapper.current.getBoundingClientRect();
      setContainerBounds((prev) =>
        !prev ||
        Math.abs(prev.width - newBounds.width) > 1 ||
        Math.abs(prev.height - newBounds.height) > 1
          ? newBounds
          : prev,
      );
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (treeWrapper.current) {
      setScrollOffset({
        x: treeWrapper.current.scrollLeft,
        y: treeWrapper.current.scrollTop,
      });
    }
  }, []);

  useEffect(() => {
    const element = treeWrapper.current;
    const observer = new ResizeObserver(() => {
      updateContainerBounds();
    });

    if (element) {
      observer.observe(element);
      element.addEventListener("scroll", handleScroll);
      // Initial bounds calculation
      updateContainerBounds();
    }

    return () => {
      if (element) {
        observer.disconnect();
        element.removeEventListener("scroll", handleScroll);
      }
    };
  }, [updateContainerBounds, handleScroll]);

  /**
   * Render the error message if data is missing
   */
  if (!props.data) {
    return <div role="alert">Failed to render tree as `data` is missing</div>;
  }

  /**
   * Default tree config if we don't have a custom one
   */
  const treeConfig = props.treeConfig || {
    edgeStart: 20,
    edgeWidth: 96,
    labelStart: 20,
  };

  return (
    <Flex className={TreeContainer}>
      <div ref={treeWrapper} className={TreeWrapper}>
        <TreeNode treeNodes={[props.data]} level={0} treeConfig={treeConfig} />
      </div>
      <TreeEdge
        edgeConfigs={edgeConfig(props.data, props.edgeStyle, props.rootEdgeStyle)}
        treeConfig={treeConfig}
        containerBounds={containerBounds}
        scrollOffset={scrollOffset}
      />
    </Flex>
  );
};
